import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { toast } from "@libs/zapi-ui/ds/components/toast";
import type { NewVersionRequest } from "@/zapi-frontend/pages/ProjectsPage/ui/ProjectVersionMenu/blocks/ProjectVersions";
import $api from "@/zapi-frontend/shared/api/axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { getRouteNewVersion, getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { useProjectVersionsStore } from "../../model/projectVersionsStore";

type TApiError = AxiosError<{ message: string }>;

export type TVersionActionsOptions = {
  projectId: string;
  organizationId: number;
  currentId: string;
  onChanged: () => void;
  onCurrentRemoved?: (nextVersionId: string | null) => void;
  onCurrentRestored?: (versionId: string) => void;
};

export function useProjectVersionActions({
  projectId,
  organizationId,
  currentId,
  onChanged,
  onCurrentRemoved,
  onCurrentRestored
}: TVersionActionsOptions) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const optimisticRename = useProjectVersionsStore((s) => s.optimisticRename);
  const optimisticRemove = useProjectVersionsStore((s) => s.optimisticRemove);

  const fail = (err: TApiError, fallback: string) =>
    toast.add({ type: "error", title: err.response?.data?.message || fallback });

  const create = async ({ name, baseId, scope }: NewVersionRequest, displayName: string) => {
    const inputsOnly = scope === "frame";
    const [result, err] = await tryCatch<{ data: { data: { id: number } } }, TApiError>(
      inputsOnly
        ? $api.post(`/projects/${baseId}/duplicate-inputs`, { initialName: name })
        : $api.post(`/projects/${baseId}/duplicate`, { organizationId, initialName: name })
    );
    if (err) {
      fail(err, t("versions.errors.createFailed"));
      return false;
    }
    toast.add({ type: "success", title: t("versions.createdToast", { name: displayName }) });
    onChanged();
    const createdId = result?.data?.data?.id;
    if (createdId)
      navigate(inputsOnly ? getRouteNewVersion(createdId) : `${getRouteProjectDetails(createdId)}?view=projectConfig`);
    return true;
  };

  const rename = async (versionId: string, name: string) => {
    const [, err] = await tryCatch<unknown, TApiError>(
      $api.patch(`/projects/${versionId}`, { versionDisplayName: name })
    );
    if (err) {
      fail(err, t("versions.errors.renameFailed"));
      return false;
    }
    optimisticRename(projectId, versionId, name);
    toast.add({ type: "success", title: t("versions.renamedToast", { name }) });
    onChanged();
    return true;
  };

  const restore = async (versionId: string, displayName: string, wasCurrent: boolean) => {
    const [, err] = await tryCatch<unknown, TApiError>(
      $api.post(`/projects/${projectId}/versions/restore`, { targetProjectId: Number(versionId) })
    );
    if (err) {
      fail(err, t("versions.errors.restoreFailed"));
      return;
    }
    toast.add({ type: "success", title: t("versions.restoredToast", { name: displayName }) });
    if (wasCurrent && onCurrentRestored) {
      onCurrentRestored(versionId);
      return;
    }
    onChanged();
  };

  const remove = async (versionId: string, displayName: string, nextVersionId: string | null) => {
    const [, err] = await tryCatch<unknown, TApiError>(
      $api.post(`/projects/${projectId}/versions/remove`, { targetProjectId: Number(versionId) })
    );
    if (err) {
      fail(err, t("versions.errors.deleteFailed"));
      return false;
    }
    const wasCurrent = versionId === currentId && Boolean(onCurrentRemoved);
    optimisticRemove(projectId, versionId);
    toast.add({
      title: t("versions.removedToast", { name: displayName }),
      actionProps: { children: t("versions.undo"), onClick: () => void restore(versionId, displayName, wasCurrent) }
    });
    if (wasCurrent) {
      onCurrentRemoved?.(nextVersionId);
      return true;
    }
    onChanged();
    return true;
  };

  return { create, rename, remove };
}
